/* eslint-disable @typescript-eslint/no-unused-vars */
import type { FormItemProp, FormRules } from 'element-plus'
import { toPairs } from 'lodash'
import { computed, reactive, type Reactive, type Ref } from 'vue'

type FormRuleable = Ref<string | Record<string, any>, string | Record<string, any>>

/**
 * Manages an `isFormValid` ref in sync with the form's rules validation state.
 * Bind the exported `onValidate` function to the form's `@validate` event, and use `isFormValid`.
 * @param formRules the reactive formRules mapping
 */
export const useFormValidationState = <T extends FormRuleable>(formRules: Reactive<FormRules<T>>) => {
  const state: Reactive<Record<string, boolean>> = reactive({})

  // Array of required keys
  const requiredFields = computed(() =>
    toPairs(formRules)
      .filter(([key, ruleset]) => (ruleset as any[]).some((rule) => rule.required))
      .map(([key, ruleset]) => key)
  )

  const isFormValid = computed(
    () =>
      // All required keys are present in state
      requiredFields.value.every((k) => Object.keys(state).includes(k)) &&
      // And every value in the state is either true or not required
      toPairs(state).every(([key, value]) => value || !requiredFields.value.includes(key))
  )

  const onValidate = (key: FormItemProp, valid: boolean) => {
    state[key as string] = valid
  }

  const clearValidationState = () => {
    Object.assign(state, {})
  }

  return { onValidate, isFormValid, clearValidationState }
}
